import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LogOut, Mail, Package, X, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function InquiriesTab({ inquiries }) {
  return (
    <div className="overflow-x-auto" data-testid="admin-inquiries">
      {inquiries.length === 0 ? (
        <p className="text-inkmute py-10">No inquiries yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wider text-inkmute border-b border-line">
              <th className="py-3 pr-4">Date</th><th className="py-3 pr-4">Type</th><th className="py-3 pr-4">Name</th>
              <th className="py-3 pr-4">Company</th><th className="py-3 pr-4">Email</th><th className="py-3 pr-4">Brand</th><th className="py-3">Message</th>
            </tr>
          </thead>
          <tbody>
            {inquiries.map((q, i) => (
              <tr key={i} className="border-b border-line/60 align-top" data-testid={`inquiry-row-${i}`}>
                <td className="py-3 pr-4 text-inkmute whitespace-nowrap">{(q.created_at || "").slice(0, 10)}</td>
                <td className="py-3 pr-4"><span className={`text-[10px] uppercase px-2 py-0.5 rounded-full ${q.inquiry_type === "export" ? "bg-clara/10 text-clara" : "bg-claris-green/10 text-claris-green"}`}>{q.inquiry_type}</span></td>
                <td className="py-3 pr-4 font-semibold text-ink">{q.name}</td>
                <td className="py-3 pr-4">{q.company || "—"}</td>
                <td className="py-3 pr-4">{q.email}</td>
                <td className="py-3 pr-4">{q.brand || "—"}</td>
                <td className="py-3 max-w-xs text-inkmute">{q.message}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function ProductEditor({ product, onClose, onSaved }) {
  const [form, setForm] = useState(product);
  const [saving, setSaving] = useState(false);

  const setLoc = (field, lng, val) => setForm((f) => ({ ...f, [field]: { ...f[field], [lng]: val } }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/products/${product.slug}`, form);
      toast.success("Product updated");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/60 flex items-center justify-center p-4" onClick={onClose} data-testid="product-editor">
      <div className="bg-cream w-full max-w-2xl max-h-[85vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-serif text-3xl text-ink">Edit · {product.slug}</h3>
          <button onClick={onClose} data-testid="editor-close"><X /></button>
        </div>

        {["name", "description", "application"].map((field) => (
          <div key={field} className="mb-5">
            <div className="text-[11px] uppercase tracking-widest text-inkmute font-semibold mb-2">{field}</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {["id", "en"].map((lng) => (
                <textarea key={lng} rows={field === "name" ? 1 : 2} value={form[field]?.[lng] || ""} onChange={(e) => setLoc(field, lng, e.target.value)}
                  placeholder={lng.toUpperCase()} data-testid={`editor-${field}-${lng}`}
                  className="w-full bg-white border border-line focus:border-ink outline-none p-3 text-sm text-ink resize-none" />
              ))}
            </div>
          </div>
        ))}

        <div className="mb-6">
          <div className="text-[11px] uppercase tracking-widest text-inkmute font-semibold mb-2">Image URL</div>
          <input value={form.image_url || ""} onChange={(e) => setForm((f) => ({ ...f, image_url: e.target.value }))} data-testid="editor-image"
            className="w-full bg-white border border-line focus:border-ink outline-none p-3 text-sm text-ink" />
        </div>

        <button onClick={save} disabled={saving} data-testid="editor-save"
          className="inline-flex items-center gap-2 bg-ink text-cream px-6 py-3 text-sm font-semibold uppercase tracking-widest hover:bg-claris transition-colors disabled:opacity-60">
          <Save size={15} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function ProductsTab({ products, refetch }) {
  const [editing, setEditing] = useState(null);
  return (
    <div data-testid="admin-products">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {products.map((p) => (
          <button key={p.slug} onClick={() => setEditing(p)} data-testid={`admin-product-${p.slug}`}
            className="text-left bg-white border border-line hover:border-ink p-5 transition-colors">
            <div className="text-[10px] uppercase tracking-wider text-inkmute">{p.brand} · {p.category}</div>
            <div className="font-serif text-xl text-ink mt-1">{p.name?.en}</div>
            <div className="text-sm text-inkmute mt-2 line-clamp-2">{p.description?.en}</div>
          </button>
        ))}
      </div>
      {editing && <ProductEditor product={editing} onClose={() => setEditing(null)} onSaved={refetch} />}
    </div>
  );
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [tab, setTab] = useState("inquiries");
  const [authed, setAuthed] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem("tpt_admin_token")) { navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => setAuthed(true)).catch(() => { localStorage.removeItem("tpt_admin_token"); navigate("/admin/login"); });
  }, [navigate]);

  const { data: inquiries = [] } = useQuery({ queryKey: ["inquiries"], queryFn: async () => (await api.get("/inquiries")).data, enabled: authed === true });
  const { data: products = [], refetch } = useQuery({ queryKey: ["admin-products"], queryFn: async () => (await api.get("/products")).data, enabled: authed === true });

  const logout = () => { localStorage.removeItem("tpt_admin_token"); navigate("/admin/login"); };

  if (authed !== true) return <div className="min-h-screen bg-cream flex items-center justify-center text-inkmute">Loading…</div>;

  return (
    <div className="min-h-screen bg-cream" data-testid="admin-dashboard">
      <header className="border-b border-line bg-white">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
          <div>
            <div className="font-serif text-2xl text-ink leading-none">Tan Putra Tama</div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-claris">Admin Console</div>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 text-sm text-inkmute hover:text-claris transition-colors"><LogOut size={16} /> Logout</button>
        </div>
      </header>

      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-10">
        <div className="flex gap-2 mb-8 border-b border-line">
          {[{ k: "inquiries", label: `Inquiries (${inquiries.length})`, icon: Mail }, { k: "products", label: `Products (${products.length})`, icon: Package }].map((tb) => (
            <button key={tb.k} onClick={() => setTab(tb.k)} data-testid={`admin-tab-${tb.k}`}
              className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${tab === tb.k ? "text-ink" : "text-inkmute hover:text-ink"}`}>
              <tb.icon size={15} /> {tb.label}
              {tab === tb.k && <span className="absolute bottom-[-1px] left-0 w-full h-0.5 bg-claris" />}
            </button>
          ))}
        </div>

        {tab === "inquiries" ? <InquiriesTab inquiries={inquiries} /> : <ProductsTab products={products} refetch={refetch} />}
      </div>
    </div>
  );
}
